import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Modal from "../common/Modal";
import LoadingSpinner from "../common/LoadingSpinner";
import apiClient from "../../services/apiClient";

const AddCustomSectionModal = ({ isOpen, onClose, documentId, sections, onSectionGenerated }) => {
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [modelsLoading, setModelsLoading] = useState(true);
  const [sectionTitle, setSectionTitle] = useState("");
  const [description, setDescription] = useState("");
  const [insertPosition, setInsertPosition] = useState("end");
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      loadAvailableModels();
      // Reset form when modal opens
      setSectionTitle("");
      setDescription("");
      setInsertPosition("end");
      setError("");
    }
  }, [isOpen]);

  const loadAvailableModels = async () => {
    try {
      setModelsLoading(true);
      const response = await apiClient.getAIModels();
      const models = response.data?.models || response.models || [];
      setAvailableModels(models);

      const defaultModel = models.find((m) => m.isDefault);
      if (defaultModel) {
        setSelectedModel(defaultModel.model);
      }
    } catch (error) {
      console.error("Failed to load available models:", error);
      setError("Failed to load AI models");
    } finally {
      setModelsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!sectionTitle.trim()) {
      setError("Section title is required");
      return;
    }

    if (!selectedModel) {
      setError("Please select an AI model");
      return;
    }

    try {
      setGenerating(true);
      setError("");

      const sectionSpec = {
        sectionTitle: sectionTitle.trim(),
        description: description.trim(),
        insertPosition: insertPosition,
        selectedModel: selectedModel
      };

      await apiClient.generateSection(documentId, sectionSpec);

      // Success!
      onSectionGenerated();
      onClose();
    } catch (error) {
      console.error("Section generation failed:", error);
      setError(error.response?.data?.message || error.message || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Custom Section" size="2xl">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Error Display */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <FontAwesomeIcon icon="exclamation-circle" className="text-red-600 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {!generating ? (
          <>
            <p className="text-sm text-gray-600">
              Add a new section to your proposal. The AI will generate content based on the RFP requirements and your existing document.
            </p>

            {/* Section Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Section Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={sectionTitle}
                onChange={(e) => setSectionTitle(e.target.value)}
                placeholder="e.g., Conclusion and Summary"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                disabled={generating}
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description (Optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Provide any specific guidance or requirements for this section..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                disabled={generating}
              />
              <p className="text-xs text-gray-500 mt-1">
                Optional: Provide specific instructions or context for what should be included in this section.
              </p>
            </div>

            {/* Insert Position */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Insert Position
              </label>
              <select
                value={insertPosition}
                onChange={(e) => setInsertPosition(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                disabled={generating}
              >
                <option value="end">At the end</option>
                {sections && sections.length > 0 && (
                  <>
                    <optgroup label="After Section">
                      {sections.map((section, index) => (
                        <option key={`after-${section.section_id}`} value={index}>
                          After: {section.section_title}
                        </option>
                      ))}
                    </optgroup>
                  </>
                )}
              </select>
            </div>

            {/* AI Model Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                AI Model <span className="text-red-500">*</span>
              </label>
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                disabled={modelsLoading || generating}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              >
                {modelsLoading ? (
                  <option value="">Loading models...</option>
                ) : (
                  <>
                    <option value="">Select AI Model...</option>
                    <optgroup label="OpenAI">
                      {availableModels
                        .filter((m) => m.provider === "openai")
                        .map((model) => (
                          <option key={model.model} value={model.model}>
                            {model.name}
                          </option>
                        ))}
                    </optgroup>
                    <optgroup label="Anthropic">
                      {availableModels
                        .filter((m) => m.provider === "anthropic")
                        .map((model) => (
                          <option key={model.model} value={model.model}>
                            {model.name}
                          </option>
                        ))}
                    </optgroup>
                    <optgroup label="Google">
                      {availableModels
                        .filter((m) => m.provider === "google")
                        .map((model) => (
                          <option key={model.model} value={model.model}>
                            {model.name}
                          </option>
                        ))}
                    </optgroup>
                  </>
                )}
              </select>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={generating}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!sectionTitle.trim() || !selectedModel || modelsLoading || generating}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <FontAwesomeIcon icon="plus-circle" />
                <span>Generate Section</span>
              </button>
            </div>
          </>
        ) : (
          <div className="py-12 text-center">
            <LoadingSpinner size="lg" />
            <p className="text-lg font-medium text-gray-900 mt-4">
              Generating "{sectionTitle}"
            </p>
            <p className="text-sm text-gray-600 mt-2">
              The AI is creating content based on RFP requirements and your document context...
            </p>
            <p className="text-xs text-gray-500 mt-4">
              This may take 1-2 minutes depending on the model selected.
            </p>
          </div>
        )}
      </form>
    </Modal>
  );
};

export default AddCustomSectionModal;
